import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('nel_token');
    if (!token) { setLoading(false); return; }
    try {
      const res = await api.get('/auth/me');
      setUser(res.data.data);
    } catch {
      localStorage.removeItem('nel_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { token, user: userData } = res.data.data;
    localStorage.setItem('nel_token', token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('nel_token');
    setUser(null);
  };

  const updateUser = (updates) => setUser(u => ({ ...u, ...updates }));

  // Role helpers
  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin' || isSuperAdmin;
  const isAdminOrMD = isAdmin || user?.sub_role === 'MD';
  const canManageUsers = isAdmin;
  const canViewAllLeads = isAdmin || ['MD', 'SVP'].includes(user?.sub_role);
  // canAssignLeads: roles that may pick an assignee when creating/editing a lead
  const canAssignLeads = canViewAllLeads || user?.sub_role === 'RCM';

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, updateUser,
      isSuperAdmin, isAdmin, isAdminOrMD, canManageUsers, canViewAllLeads, canAssignLeads
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
